import React from "react";

import DeviceAssignmentPicker from "../devices/deviceAssignmentPicker";

function cleanAccessList(list = []) {
  return (list || []).filter((entry) => entry && entry !== "All PDUs");
}

function getDeviceSerial(device = {}) {
  const serial = device.enclosureSerialNumber ?? device.serial;
  return serial == null ? "" : String(serial);
}

function formatDeviceLabel(accessEntry, devices = []) {
  const serial = String(accessEntry).replace("PDU:", "");
  const device = (devices || []).find((item) => getDeviceSerial(item) === serial);
  if (!device) {
    return `PDU ${serial}`;
  }

  const model = device.modelNumber ? `SP-${device.modelNumber}` : "PDU";
  const name = device.deviceName || device.name || "";
  return `${model} (${serial})${name ? ` ${name}` : ""}`;
}

function selectedGroupsCount(accessList = []) {
  return accessList.filter((entry) => String(entry).startsWith("Group:")).length;
}

function selectedDevicesCount(accessList = []) {
  return accessList.filter((entry) => String(entry).startsWith("PDU:")).length;
}

export default class PDUAccessDropdown extends React.Component {
  state = {
    modalOpen: false,
  };

  openModal = (accessList = []) => {
    this.setState({
      modalOpen: true,
      modalMode: cleanAccessList(accessList).length > 0 ? "specific" : "all",
    });
  };

  getDeviceGroups = () => {
    const { groups = [] } = this.props;
    return groups.filter((group) => group.groupType == "devices");
  };

  getGroupLabel = (accessEntry) => {
    const groupId = String(accessEntry).replace("Group:", "");
    const group = this.getDeviceGroups().find((item) => String(item.id) === groupId);
    return group?.name || accessEntry;
  };

  updateAccessList = (nextList) => {
    const cleanedList = cleanAccessList(nextList);
    this.setState({
      modalMode: cleanedList.length > 0 ? "specific" : "all",
    });
    this.props.onPduAccessListChange(cleanedList);
  };

  renderAccessPill = (entry, label, editable = false) => (
    <span key={entry} style={styles.pill}>
      <span style={styles.pillLabel}>{label}</span>
      {editable ? (
        <button
          type="button"
          style={styles.pillButton}
          onClick={() => {
            this.updateAccessList(
              cleanAccessList(this.props.pdu_access_list).filter(
                (item) => item !== entry,
              ),
            );
          }}
          aria-label={`Remove ${label}`}
        >
          <i className="material-icons" style={styles.pillIcon}>
            close
          </i>
        </button>
      ) : null}
    </span>
  );

  renderEntryPill = (entry, editable = false) => {
    const { devices = [] } = this.props;
    const label = String(entry).startsWith("Group:")
      ? this.getGroupLabel(entry)
      : formatDeviceLabel(entry, devices);
    return this.renderAccessPill(entry, label, editable);
  };

  renderReadOnly = (accessList) => {
    const cleanedList = cleanAccessList(accessList);

    return (
      <div style={styles.readOnlyWrap}>
        {cleanedList.length === 0
          ? this.renderAccessPill("all-pdus", "All PDUs")
          : cleanedList.map((entry) => this.renderEntryPill(entry))}
      </div>
    );
  };

  renderCompactSummary = (accessList) => {
    const cleanedList = cleanAccessList(accessList);
    const visibleEntries = cleanedList.slice(0, 4);
    const hiddenCount = Math.max(cleanedList.length - visibleEntries.length, 0);

    return (
      <div style={styles.summaryCard}>
        <div style={styles.summaryHeader}>
          <div>
            <div style={styles.summaryTitle}>Current access</div>
            <div style={styles.summaryMeta}>
              {cleanedList.length === 0
                ? "All PDUs"
                : `${selectedGroupsCount(cleanedList)} groups, ${selectedDevicesCount(cleanedList)} individual PDUs`}
            </div>
          </div>
          <button
            type="button"
            className="button secondary"
            style={styles.manageButton}
            onClick={() => this.openModal(accessList)}
          >
            <i className="material-icons" style={styles.buttonIcon}>
              tune
            </i>
            Manage access
          </button>
        </div>
        <div style={styles.summaryChips}>
          {cleanedList.length === 0
            ? this.renderAccessPill("all-pdus", "All PDUs")
            : visibleEntries.map((entry) => this.renderEntryPill(entry))}
          {hiddenCount > 0 ? (
            <span style={{ ...styles.pill, ...styles.countPill }}>
              +{hiddenCount} more
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  renderModal = ({
    accessList,
    deviceGroups,
    devices,
    editable,
    onPduAccessListChange,
    selectedDeviceIds,
    selectedGroups,
    updateSelectedDevices,
  }) => {
    if (!this.state.modalOpen) {
      return null;
    }

    const isAllAccess = accessList.length === 0 && this.state.modalMode !== "specific";
    const unselectedDeviceGroups = deviceGroups.filter(
      (group) =>
        !selectedGroups.find(
          (entry) => entry === `Group:${group.id}`,
        ),
    );

    return (
      <div
        style={styles.modalOverlay}
        onClick={() => this.setState({ modalOpen: false })}
      >
        <div
          style={styles.modalPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div style={styles.modalHeader}>
            <div>
              <div style={styles.modalTitle}>Manage device access</div>
            </div>
            <button
              type="button"
              style={styles.closeButton}
              onClick={() => this.setState({ modalOpen: false })}
              aria-label="Close device access manager"
            >
              <i className="material-icons" style={styles.closeIcon}>
                close
              </i>
            </button>
          </div>

          <div style={styles.modalBody}>
            <div style={styles.modalSection}>
              <div style={styles.sectionTitle}>Access mode</div>
              <div style={styles.modeGrid}>
                <button
                  type="button"
                  style={{
                    ...styles.modeCard,
                    ...(isAllAccess ? styles.modeCardActive : {}),
                  }}
                  onClick={() => {
                    this.setState({ modalMode: "all" });
                    onPduAccessListChange([]);
                  }}
                >
                  <span style={styles.modeTitle}>All PDUs</span>
                  <span style={styles.modeState}>
                    {isAllAccess ? "Selected" : "Available"}
                  </span>
                </button>
                <div
                  style={{
                    ...styles.modeCard,
                    ...(!isAllAccess ? styles.modeCardActive : {}),
                  }}
                >
                  <span style={styles.modeTitle}>Specific access</span>
                  <span style={styles.modeState}>
                    {!isAllAccess ? "Selected" : "Not selected"}
                  </span>
                </div>
              </div>
            </div>

            <div style={styles.modalSection}>
              <div style={styles.groupHeader}>
                <div style={styles.sectionTitle}>PDU groups</div>
                <div style={styles.sectionMeta}>
                  {selectedGroups.length} selected
                </div>
              </div>
              <div style={styles.groupSelectWrap}>
                <select
                  aria-label="Add PDU group"
                  value=""
                  style={styles.groupSelect}
                  disabled={unselectedDeviceGroups.length === 0}
                  onChange={(event) => {
                    const nextGroup = event.target.value;
                    if (!nextGroup) {
                      return;
                    }
                    this.updateAccessList([...accessList, nextGroup]);
                  }}
                >
                  <option value="">Add PDU group</option>
                  {unselectedDeviceGroups
                    .map((group) => (
                      <option key={group.id} value={`Group:${group.id}`}>
                        {group.name}
                      </option>
                    ))}
                </select>
              </div>
              {selectedGroups.length === 0 ? (
                <div style={styles.emptyText}>No PDU groups selected.</div>
              ) : (
                <div style={styles.readOnlyWrap}>
                  {selectedGroups.map((entry) =>
                    this.renderAccessPill(entry, this.getGroupLabel(entry), true),
                  )}
                </div>
              )}
            </div>

            <div style={styles.modalSection}>
              <DeviceAssignmentPicker
                selectedDeviceIds={selectedDeviceIds}
                onSelectedDeviceIdsChange={updateSelectedDevices}
                initialDevices={devices}
                editable={editable}
              />
            </div>
          </div>

          <div style={styles.modalFooter}>
            <button
              type="button"
              className="button"
              style={styles.doneButton}
              onClick={() => this.setState({ modalOpen: false })}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      pdu_access_list,
      onPduAccessListChange = () => {},
      devices = [],
      editable = true,
    } = this.props;

    const accessList = cleanAccessList(pdu_access_list);
    const deviceGroups = this.getDeviceGroups();
    const selectedGroups = accessList.filter((entry) =>
      String(entry).startsWith("Group:"),
    );
    const selectedDeviceIds = accessList
      .filter((entry) => String(entry).startsWith("PDU:"))
      .map((entry) => String(entry).replace("PDU:", ""));

    if (!editable) {
      return this.renderReadOnly(pdu_access_list);
    }

    const updateSelectedDevices = (nextDeviceIds) => {
      const nextDeviceEntries = nextDeviceIds.map((serial) => `PDU:${serial}`);
      this.updateAccessList([...selectedGroups, ...nextDeviceEntries]);
    };

    return (
      <div style={styles.wrapper}>
        {this.renderCompactSummary(accessList)}
        {this.renderModal({
          accessList,
          deviceGroups,
          devices,
          editable,
          onPduAccessListChange,
          selectedDeviceIds,
          selectedGroups,
          updateSelectedDevices,
        })}
      </div>
    );
  }
}

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "14px",
    width: "100%",
  },
  section: {
    width: "100%",
  },
  sectionTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: "var(--app-text)",
    marginBottom: "8px",
  },
  readOnlyWrap: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px",
    minHeight: "24px",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontWeight: 500,
    borderRadius: "5px",
    backgroundColor: "#223647",
    color: "white",
    fontSize: "13px",
    padding: "3px 4px 3px 7px",
  },
  countPill: {
    backgroundColor: "#4b5563",
    paddingRight: "7px",
  },
  pillLabel: {
    overflowWrap: "anywhere",
  },
  pillButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    color: "white",
    padding: "0",
    cursor: "pointer",
    width: "18px",
    height: "18px",
  },
  pillIcon: {
    fontSize: "14px",
    marginRight: "0",
  },
  groupSelect: {
    marginBottom: 0,
    minWidth: 0,
    width: "min(280px, 100%)",
  },
  groupSelectWrap: {
    marginBottom: "10px",
    maxWidth: "320px",
  },
  groupHeader: {
    marginBottom: "8px",
  },
  emptyText: {
    color: "var(--text-soft-color, #6b7280)",
    fontSize: "13px",
  },
  summaryCard: {
    border: "1px solid var(--border-color, #e5e7eb)",
    borderRadius: "8px",
    padding: "12px",
    background: "var(--app-surface-1)",
    width: "100%",
  },
  summaryHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "10px",
  },
  summaryTitle: {
    color: "var(--app-text)",
    fontSize: "13px",
    fontWeight: 700,
  },
  summaryMeta: {
    color: "var(--text-soft-color, #6b7280)",
    fontSize: "12px",
    marginTop: "2px",
  },
  summaryChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    minWidth: 0,
  },
  manageButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: 0,
    whiteSpace: "nowrap",
  },
  buttonIcon: {
    fontSize: "16px",
    margin: 0,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1600,
    background: "var(--app-modal-overlay)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
  },
  modalPanel: {
    width: "min(900px, 100%)",
    maxHeight: "calc(100vh - 48px)",
    background: "var(--app-modal-bg)",
    border: "1px solid var(--app-border)",
    color: "var(--app-text)",
    borderRadius: "8px",
    boxShadow: "0 20px 50px rgba(15, 23, 42, 0.35)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    padding: "18px 22px",
    borderBottom: "1px solid var(--border-color, #e5e7eb)",
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: 700,
    color: "var(--app-text)",
  },
  closeButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "transparent",
    color: "var(--text-soft-color, #6b7280)",
    cursor: "pointer",
    width: "32px",
    height: "32px",
    padding: 0,
  },
  closeIcon: {
    fontSize: "20px",
    margin: 0,
  },
  modalBody: {
    padding: "20px 22px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  modalSection: {
    border: "1px solid var(--border-color, #e5e7eb)",
    borderRadius: "8px",
    padding: "14px",
    background: "var(--app-surface-1)",
  },
  modeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "10px",
  },
  modeCard: {
    border: "1px solid var(--border-color, #d1d5db)",
    borderRadius: "6px",
    background: "transparent",
    color: "var(--app-text)",
    padding: "12px",
    textAlign: "left",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
  },
  modeCardActive: {
    border: "1px solid var(--app-border-strong)",
    background: "var(--app-surface-3)",
  },
  modeTitle: {
    fontSize: "14px",
    fontWeight: 700,
  },
  modeState: {
    fontSize: "12px",
    color: "var(--text-soft-color, #6b7280)",
    whiteSpace: "nowrap",
  },
  sectionMeta: {
    color: "var(--text-soft-color, #6b7280)",
    fontSize: "12px",
    marginTop: "2px",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "14px 22px",
    borderTop: "1px solid var(--border-color, #e5e7eb)",
  },
  doneButton: {
    marginBottom: 0,
  },
};
